import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Target, X, Zap, Trophy, Activity } from 'lucide-react';

// Simple themed mini-game. Activated by Konami code or typing 'ASTRA'.
// Holographic cyan targets spawn in a bounded area; click to lock-on for points.
// Keeps Astra (3D canvas) visible behind — just a HUD overlay.

const GAME_DURATION = 30; // seconds
const TARGET_LIFETIME = 1.8; // seconds
const SPAWN_INTERVAL = 0.55; // seconds

const KONAMI = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
  'b', 'a',
];

function useActivation(onActivate) {
  useEffect(() => {
    let seq = [];
    let typed = '';
    const onKey = (e) => {
      // Ignore when typing in inputs
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      // Konami check
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      seq.push(key);
      if (seq.length > KONAMI.length) seq = seq.slice(-KONAMI.length);
      if (seq.length === KONAMI.length && seq.every((k, i) => k === KONAMI[i])) {
        onActivate();
        seq = [];
        typed = '';
        return;
      }
      // Type ASTRA
      if (/^[a-zA-Z]$/.test(e.key)) {
        typed = (typed + e.key.toUpperCase()).slice(-5);
        if (typed === 'ASTRA') {
          onActivate();
          typed = '';
          seq = [];
        }
      } else {
        typed = '';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onActivate]);
}

function spawnTarget(w, h) {
  const pad = 90;
  return {
    id: Math.random().toString(36).slice(2),
    x: pad + Math.random() * (w - pad * 2),
    y: pad + Math.random() * (h - pad * 2),
    r: 28 + Math.random() * 22,
    bornAt: performance.now(),
  };
}

export default function EasterEggGame() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle'); // idle | playing | gameover
  const [time, setTime] = useState(GAME_DURATION);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [targets, setTargets] = useState([]);
  const [hi, setHi] = useState(() => {
    try {
      return parseInt(localStorage.getItem('astra_hi') || '0', 10) || 0;
    } catch (error) {
      console.warn('[AstraGame] could not read high score:', error);
      return 0;
    }
  });
  const [flash, setFlash] = useState(null);

  const arenaRef = useRef(null);
  const lastSpawn = useRef(0);
  const rafRef = useRef(0);
  const startedAt = useRef(0);

  const openGame = useCallback(() => {
    setOpen(true);
    setState('idle');
    setTime(GAME_DURATION);
    setScore(0);
    setCombo(0);
    setBestCombo(0);
    setHits(0);
    setMisses(0);
    setTargets([]);
  }, []);

  useActivation(openGame);

  const closeGame = useCallback(() => {
    setOpen(false);
    setState('idle');
  }, []);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeGame();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeGame]);

  // Game loop
  useEffect(() => {
    if (!open || state !== 'playing') return;
    startedAt.current = performance.now();
    lastSpawn.current = 0;

    const tick = () => {
      const arena = arenaRef.current;
      if (!arena) return;
      const { width, height } = arena.getBoundingClientRect();
      const now = performance.now();
      const elapsed = (now - startedAt.current) / 1000;
      const remaining = Math.max(0, GAME_DURATION - elapsed);
      setTime(remaining);

      // Spawn targets at interval; spawn more as time progresses
      const interval = Math.max(0.28, SPAWN_INTERVAL - elapsed * 0.012);
      if (now - lastSpawn.current > interval * 1000) {
        lastSpawn.current = now;
        setTargets((prev) => {
          if (prev.length >= 6) return prev;
          return [...prev, spawnTarget(width, height)];
        });
      }

      // Expire old targets as misses
      setTargets((prev) => {
        const alive = [];
        let expired = 0;
        for (const t of prev) {
          if ((now - t.bornAt) / 1000 < TARGET_LIFETIME) alive.push(t);
          else expired++;
        }
        if (expired > 0) {
          setMisses((m) => m + expired);
          setCombo(0);
        }
        return alive;
      });

      if (remaining <= 0) {
        setState('gameover');
        setTargets([]);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, state]);

  // Persist high score on gameover
  useEffect(() => {
    if (state !== 'gameover') return;
    setHi((prev) => {
      const next = Math.max(prev, score);
      try {
        localStorage.setItem('astra_hi', String(next));
      } catch (error) {
        console.warn('[AstraGame] could not persist high score:', error);
      }
      return next;
    });
  }, [state, score]);

  const accuracy = useMemo(() => {
    const tot = hits + misses;
    if (!tot) return 0;
    return Math.round((hits / tot) * 100);
  }, [hits, misses]);

  const onHit = useCallback((t, e) => {
    e.stopPropagation();
    setTargets((prev) => prev.filter((x) => x.id !== t.id));
    setHits((h) => h + 1);
    setCombo((c) => {
      const next = c + 1;
      setBestCombo((b) => Math.max(b, next));
      return next;
    });
    const bonus = Math.min(50, combo * 2);
    const age = (performance.now() - t.bornAt) / 1000;
    const speedBonus = Math.round(Math.max(0, (TARGET_LIFETIME - age) / TARGET_LIFETIME) * 15);
    const points = 10 + bonus + speedBonus;
    setScore((s) => s + points);
    setFlash({ x: t.x, y: t.y, points, key: Math.random() });
    setTimeout(() => setFlash(null), 550);
  }, [combo]);

  const onArenaMiss = useCallback(() => {
    if (state !== 'playing') return;
    setMisses((m) => m + 1);
    setCombo(0);
  }, [state]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120]"
      style={{ pointerEvents: 'auto' }}
      aria-label="Protocol Target Lock mini-game"
    >
      {/* Dim scrim — lighter than a modal so Astra glows through */}
      <div className="absolute inset-0 bg-[#02060b]/55 backdrop-blur-[1px]" />

      {/* Top HUD */}
      <div className="absolute top-0 inset-x-0 px-6 md:px-10 py-5 flex items-center justify-between text-cyan-100">
        <div className="flex items-center gap-3 mono text-[11px] tracking-[0.3em]">
          <span className="inline-flex items-center gap-2">
            <Target size={14} className="text-cyan-300" />
            PROTOCOL: TARGET&nbsp;LOCK
          </span>
          <span className="text-cyan-400/50">//</span>
          <span className="text-cyan-300">v1.0</span>
        </div>
        <button
          onClick={closeGame}
          className="btn-ghost rounded-md px-3 py-2 mono text-[10px] tracking-[0.3em] inline-flex items-center gap-2"
        >
          <X size={12} /> EXIT [ESC]
        </button>
      </div>

      {/* Stats bar */}
      <div className="absolute top-16 inset-x-0 px-6 md:px-10">
        <div className="glass rounded-md px-5 py-3 flex flex-wrap items-center gap-6 mono text-[11px] tracking-[0.3em] text-cyan-100">
          <Stat icon={<Activity size={12} />} label="TIME" value={`${time.toFixed(1)}s`} />
          <Stat icon={<Zap size={12} />} label="SCORE" value={score} accent />
          <Stat label="COMBO" value={`x${combo}`} />
          <Stat label="BEST" value={`x${bestCombo}`} />
          <Stat label="ACC" value={`${accuracy}%`} />
          <Stat icon={<Trophy size={12} />} label="HI" value={Math.max(hi, score)} />
        </div>
      </div>

      {/* Arena */}
      <div
        ref={arenaRef}
        onClick={onArenaMiss}
        className="absolute inset-x-6 md:inset-x-10 top-32 bottom-24 rounded-xl border border-cyan-400/25 overflow-hidden tech-grid"
        style={{ cursor: state === 'playing' ? 'crosshair' : 'default', background: 'rgba(2,10,16,0.35)' }}
      >
        {state === 'idle' && (
          <Centered>
            <div className="text-center max-w-md">
              <div className="mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">CLASSIFIED // TRAINING MODE</div>
              <h3 className="display text-3xl text-cyan-50 holo-text mb-3">PROTOCOL: TARGET LOCK</h3>
              <p className="text-cyan-100/80 text-sm leading-relaxed mb-6">
                Click the holographic sigils before they decay. Fast locks earn speed bonuses. Chains build combo multipliers. Missed locks break the streak.
              </p>
              <button
                onClick={() => setState('playing')}
                className="btn-primary rounded-md px-6 py-3 mono text-[11px] tracking-[0.3em] uppercase"
              >
                Initiate Sequence
              </button>
              <div className="mt-6 mono text-[10px] tracking-[0.3em] text-cyan-400/60">
                30 SECONDS &middot; ESC TO ABORT
              </div>
            </div>
          </Centered>
        )}

        {state === 'playing' && targets.map((t) => (
          <TargetRing key={t.id} target={t} onHit={onHit} />
        ))}

        {flash && <FloatingScore x={flash.x} y={flash.y} points={flash.points} />}

        {state === 'gameover' && (
          <Centered>
            <div className="text-center max-w-md">
              <div className="mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">SEQUENCE COMPLETE</div>
              <h3 className="display text-3xl text-cyan-50 holo-text mb-5">
                {score >= hi ? 'NEW HIGH SIGNAL' : 'TRANSMISSION ENDED'}
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <Tile label="FINAL SCORE" value={score} accent />
                <Tile label="HIGH SCORE" value={Math.max(hi, score)} />
                <Tile label="BEST COMBO" value={`x${bestCombo}`} />
                <Tile label="ACCURACY" value={`${accuracy}%`} />
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => { setState('idle'); setScore(0); setCombo(0); setBestCombo(0); setHits(0); setMisses(0); setTime(GAME_DURATION); setTargets([]); }}
                  className="btn-primary rounded-md px-5 py-2.5 mono text-[11px] tracking-[0.3em] uppercase"
                >
                  Re-Engage
                </button>
                <button
                  onClick={closeGame}
                  className="btn-ghost rounded-md px-5 py-2.5 mono text-[11px] tracking-[0.3em] uppercase"
                >
                  Stand Down
                </button>
              </div>
            </div>
          </Centered>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 inset-x-0 text-center mono text-[10px] tracking-[0.4em] text-cyan-400/60">
        KONAMI &uarr;&uarr;&darr;&darr;&larr;&rarr;&larr;&rarr;BA &nbsp;//&nbsp; OR TYPE &quot;ASTRA&quot; ANYWHERE
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-2">
      {icon && <span className="text-cyan-300">{icon}</span>}
      <span className="text-cyan-400/70">{label}</span>
      <span className={accent ? 'text-cyan-200 text-sm' : 'text-cyan-100'}>{value}</span>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="glass rounded-md p-3 text-center">
      <div className="mono text-[10px] tracking-[0.35em] text-cyan-400/80">{label}</div>
      <div className={`display ${accent ? 'text-3xl text-cyan-50' : 'text-xl text-cyan-100'} mt-1 holo-text`}>{value}</div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-4">{children}</div>
  );
}

function TargetRing({ target, onHit }) {
  const [age, setAge] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => {
      if (!alive) return;
      setAge((performance.now() - target.bornAt) / 1000);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { alive = false; };
  }, [target.bornAt]);

  const progress = Math.min(1, age / TARGET_LIFETIME);
  const scale = 1 - progress * 0.35;
  const opacity = 1 - Math.pow(progress, 2.2);

  return (
    <button
      onClick={(e) => onHit(target, e)}
      className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{
        left: target.x,
        top: target.y,
        width: target.r * 2,
        height: target.r * 2,
        transform: `translate(-50%,-50%) scale(${scale})`,
        opacity,
        background:
          'radial-gradient(closest-side, rgba(34,211,238,0.35), rgba(34,211,238,0.08) 65%, transparent 70%)',
        border: '1px solid rgba(34,211,238,0.75)',
        boxShadow: '0 0 24px rgba(34,211,238,0.55), inset 0 0 12px rgba(34,211,238,0.35)',
        cursor: 'crosshair',
      }}
      aria-label="target"
    >
      <span className="absolute inset-0 rounded-full border border-cyan-200/30 animate-ping" />
      <span className="absolute inset-2 rounded-full border border-cyan-300/40" />
      <span className="absolute inset-0 grid place-items-center mono text-[9px] tracking-[0.3em] text-cyan-100">
        LOCK
      </span>
    </button>
  );
}

function FloatingScore({ x, y, points }) {
  return (
    <div
      className="absolute pointer-events-none display text-cyan-200 holo-text"
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%,-50%)',
        animation: 'floatUp 550ms ease-out forwards',
        textShadow: '0 0 12px rgba(34,211,238,0.8)',
        fontSize: 20,
      }}
    >
      +{points}
      <style>{`@keyframes floatUp { from { transform: translate(-50%,-50%) scale(1); opacity: 1 } to { transform: translate(-50%,-140%) scale(1.1); opacity: 0 } }`}</style>
    </div>
  );
}
